import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './AuthModal.css';
import emailIcon from '../../assets/images/logos/email-icon.png';
import { isAdminPanelRole } from '../../utils/adminRoles';

const DISPOSABLE_EMAIL_DOMAINS = [
    'tempmail.com', 'throwaway.com', 'mailinator.com', 'guerrillamail.com',
    'fakeinbox.com', '10minutemail.com', 'trashmail.com', 'getnada.com',
    'temp-mail.org', 'disposablemail.com', 'yopmail.com', 'sharklasers.com',
    'guerrillamail.info', 'grr.la', 'spam4.me', 'pokemail.net', 'dispostable.com',
    'mailnesia.com', 'tempail.com', 'emailondeck.com', 'tempr.email'
];

const INVALID_DOMAIN_PATTERNS = [
    /^\/test\./, /^\/fake\./, /^\/invalid\./, /^\/example\./, /^\/sample\./,
    /\.test$/, /\.fake$/, /\.invalid$/, /\.example$/, /\.local$/
];

function AuthModal({ isOpen, onClose }) {
    const [activeTab, setActiveTab] = useState('login');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [verificationEmail, setVerificationEmail] = useState('');
    const [isClosing, setIsClosing] = useState(false);
    const [emailValidation, setEmailValidation] = useState({ isValid: null, message: '' });
    const [phoneValidation, setPhoneValidation] = useState({ isValid: null, message: '' });
    const [passwordValidation, setPasswordValidation] = useState({ isValid: null, message: '' });
    const [showTermsModal, setShowTermsModal] = useState(false);
    
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        contactNumber: '',
        password: '',
        rememberMe: false,
        agreeToPromotions: false,
        agreeToTerms: false
    });

    const { signUp, logIn, resendVerificationEmail, sendPasswordResetEmail, validatePhilippineNumber, currentUser, userRole, loading } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (isOpen) setIsClosing(false);
    }, [isOpen]);

    useEffect(() => {
        if (!loading && currentUser && isAdminPanelRole(userRole) && !isOpen) {
            navigate('/admin', { replace: true });
        }
    }, [currentUser, userRole, loading, isOpen, navigate]);

    if (!isOpen && !isClosing) return null;

    const handleInputChange = (event) => {
        const { name, value, type, checked } = event.target;
        setFormData(previousData => ({
            ...previousData,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (error) setError('');
    };

    const handleLoginSubmit = async (event) => {
        event.preventDefault();
        setError('');
        if (!formData.email.trim()) { setError('Please enter your email address.'); return; }
        if (!formData.password) { setError('Please enter your password.'); return; }
        setIsLoading(true);
        try {
            const result = await logIn(formData.email.trim(), formData.password);
            if (result.error) {
                setError(result.error);
                if (result.needsVerification) { setVerificationEmail(result.email); setActiveTab('verify'); }
            } else { resetForm(); onClose(); }
        } catch { setError('An unexpected error occurred. Please try again.'); }
        finally { setIsLoading(false); }
    };

    const handleForgotPasswordSubmit = async (email) => {
        setError(''); setSuccessMessage(''); setIsLoading(true);
        try {
            const result = await sendPasswordResetEmail(email.trim());
            if (result && result.error) setError(result.error);
            else setSuccessMessage('Password reset email sent! Please check your inbox.');
        } catch { setError('An unexpected error occurred. Please try again.'); }
        finally { setIsLoading(false); }
    };

    const isValidEmailFormat = (email) => {
        const emailRegex = /^[a-zA-Z0-9](?:[a-zA-Z0-9._-]*[a-zA-Z0-9])?@[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,10}$/;
        return emailRegex.test(email);
    };

    const isDisposableEmail = (email) => {
        const domain = email.toLowerCase().split('@')[1];
        if (!domain) return false;
        return DISPOSABLE_EMAIL_DOMAINS.includes(domain);
    };

    const hasInvalidDomainPattern = (email) => {
        const domain = email.toLowerCase().split('@')[1];
        if (!domain) return false;
        return INVALID_DOMAIN_PATTERNS.some(pattern => pattern.test(domain));
    };

    const validateEmail = (email) => {
        const trimmedEmail = email.trim();
        if (!trimmedEmail) return { isValid: false, message: 'Please enter your email address.' };
        if (!isValidEmailFormat(trimmedEmail)) return { isValid: false, message: 'Please enter a valid email address (e.g., name@example.com).' };
        if (isDisposableEmail(trimmedEmail)) return { isValid: false, message: 'Disposable email addresses are not allowed. Please use a permanent email.' };
        if (hasInvalidDomainPattern(trimmedEmail)) return { isValid: false, message: 'Please enter a real email address from a valid domain.' };
        const localPart = trimmedEmail.split('@')[0].toLowerCase();
        const fakePatterns = ['test', 'fake', 'invalid', 'asdf', 'qwerty', 'aaa', 'xxx', '123'];
        if (fakePatterns.some(pattern => localPart === pattern)) return { isValid: false, message: 'Please enter your real email address.' };
        return { isValid: true, message: '' };
    };

    const handleEmailBlur = (event) => {
        const email = event.target.value;
        if (email.trim()) setEmailValidation(validateEmail(email));
    };

    const handleEmailChange = (event) => {
        const { value } = event.target;
        setFormData(prev => ({ ...prev, email: value }));
        if (error) setError('');
        if (value.trim().length > 5 && value.includes('@')) setEmailValidation(validateEmail(value));
        else if (value.trim().length === 0) setEmailValidation({ isValid: null, message: '' });
    };

    const handlePhoneValidation = (phone) => {
        if (!phone.trim()) { setPhoneValidation({ isValid: null, message: '' }); return { isValid: null, message: '' }; }
        const result = validatePhilippineNumber(phone);
        setPhoneValidation({ isValid: result.isValid, message: result.message });
        return result;
    };

    const handlePhoneChange = (event) => {
        const { value } = event.target;
        const cleanedValue = value.replace(/[^\d+\s-]/g, '');
        setFormData(prev => ({ ...prev, contactNumber: cleanedValue }));
        if (error) setError('');
        if (cleanedValue.replace(/[\s-]/g, '').length >= 10) handlePhoneValidation(cleanedValue);
        else setPhoneValidation({ isValid: null, message: '' });
    };

    const handlePhoneBlur = (event) => {
        const phone = event.target.value;
        if (phone.trim()) handlePhoneValidation(phone);
    };

    const validatePassword = (password) => {
        if (password.length < 8) return { isValid: false, message: 'Password must be at least 8 characters long.' };
        if (!/[A-Z]/.test(password)) return { isValid: false, message: 'Password must contain at least one uppercase letter.' };
        if (!/[a-z]/.test(password)) return { isValid: false, message: 'Password must contain at least one lowercase letter.' };
        if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(password)) return { isValid: false, message: 'Password must contain at least one symbol (e.g., !@#$%^&*).' };
        return { isValid: true, message: '' };
    };

    const handlePasswordChange = (event) => {
        const { value } = event.target;
        setFormData(prev => ({ ...prev, password: value }));
        if (error) setError('');
        if (value.length > 0) setPasswordValidation(validatePassword(value));
        else setPasswordValidation({ isValid: null, message: '' });
    };

    const validateSignUpForm = () => {
        if (!formData.firstName.trim()) return { isValid: false, message: 'Please enter your first name.' };
        if (!formData.lastName.trim()) return { isValid: false, message: 'Please enter your last name.' };
        const emailValidationResult = validateEmail(formData.email);
        if (!emailValidationResult.isValid) { setEmailValidation(emailValidationResult); return emailValidationResult; }
        if (!formData.contactNumber.trim()) return { isValid: false, message: 'Please enter your contact number.' };
        const phoneValidationResult = validatePhilippineNumber(formData.contactNumber);
        if (!phoneValidationResult.isValid) { setPhoneValidation(phoneValidationResult); return { isValid: false, message: phoneValidationResult.message }; }
        if (!formData.password) return { isValid: false, message: 'Please enter a password.' };
        const passwordValidationCheck = validatePassword(formData.password);
        if (!passwordValidationCheck.isValid) return passwordValidationCheck;
        if (!formData.agreeToTerms) return { isValid: false, message: 'You must agree to the Terms and Conditions to sign up.' };
        return { isValid: true, message: '' };
    };

    const handleSignUpSubmit = async (event) => {
        event.preventDefault();
        setError(''); setSuccessMessage('');
        const validation = validateSignUpForm();
        if (!validation.isValid) { setError(validation.message); return; }
        setIsLoading(true);
        try {
            const userData = {
                firstName: formData.firstName.trim(),
                lastName: formData.lastName.trim(),
                contactNumber: formData.contactNumber.trim(),
                agreeToPromotions: formData.agreeToPromotions
            };
            const result = await signUp(formData.email.trim(), formData.password, userData);
            if (result.error) setError(result.error);
            else { setVerificationEmail(formData.email.trim()); setActiveTab('verify'); }
        } catch { setError('An unexpected error occurred. Please try again.'); }
        finally { setIsLoading(false); }
    };

    const handleResendVerification = async () => {
        setError(''); setSuccessMessage(''); setIsLoading(true);
        try {
            const result = await resendVerificationEmail(verificationEmail, formData.password);
            if (result.error) setError(result.error);
            else setSuccessMessage(result.message || 'Verification email sent! Please check your inbox.');
        } catch { setError('Failed to resend verification email. Please try again.'); }
        finally { setIsLoading(false); }
    };

    const resetForm = () => {
        setFormData({ firstName: '', lastName: '', email: '', contactNumber: '', password: '', rememberMe: false, agreeToPromotions: false, agreeToTerms: false });
        setError(''); setSuccessMessage(''); setShowPassword(false); setVerificationEmail('');
        setEmailValidation({ isValid: null, message: '' });
        setPhoneValidation({ isValid: null, message: '' });
        setPasswordValidation({ isValid: null, message: '' });
        setShowTermsModal(false);
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab); setError(''); setSuccessMessage('');
        setEmailValidation({ isValid: null, message: '' });
        setPhoneValidation({ isValid: null, message: '' });
        setPasswordValidation({ isValid: null, message: '' });
    };

    const handleOverlayClick = (event) => {
        if (event.target === event.currentTarget) { resetForm(); onClose(); }
    };

    const handleCloseClick = () => { resetForm(); onClose(); };

    return (
        <div className="auth-modal-overlay" onClick={handleOverlayClick}>
            <div className={`auth-modal ${activeTab === 'signup' ? 'signup-active' : ''}`}>
                <button className="auth-modal-close" onClick={handleCloseClick} aria-label="Close modal">
                    <CloseIcon />
                </button>

                {activeTab !== 'verify' && activeTab !== 'forgotPassword' && (
                    <div className="auth-tabs">
                        <div className={`tab-indicator ${activeTab === 'login' ? 'login-active' : 'signup-active'}`}></div>
                        <button className={`auth-tab ${activeTab === 'login' ? 'active' : ''}`} onClick={() => handleTabChange('login')} disabled={isLoading}>Log In</button>
                        <button className={`auth-tab ${activeTab === 'signup' ? 'active' : ''}`} onClick={() => handleTabChange('signup')} disabled={isLoading}>Sign Up</button>
                    </div>
                )}

                {error && <div className="auth-error-message">{error}</div>}
                {successMessage && <div className="auth-success-message">{successMessage}</div>}

                {activeTab === 'login' ? (
                    <LoginForm
                        formData={formData} showPassword={showPassword} isLoading={isLoading}
                        onInputChange={handleInputChange} onTogglePassword={() => setShowPassword(p => !p)}
                        onSubmit={handleLoginSubmit} onForgotPassword={() => handleTabChange('forgotPassword')}
                    />
                ) : activeTab === 'signup' ? (
                    <>
                        <SignUpForm
                            formData={formData} showPassword={showPassword} isLoading={isLoading}
                            onInputChange={handleInputChange}
                            onEmailChange={handleEmailChange} onEmailBlur={handleEmailBlur} emailValidation={emailValidation}
                            onPhoneChange={handlePhoneChange} onPhoneBlur={handlePhoneBlur} phoneValidation={phoneValidation}
                            onPasswordChange={handlePasswordChange} passwordValidation={passwordValidation}
                            onTogglePassword={() => setShowPassword(p => !p)}
                            onSubmit={handleSignUpSubmit} onShowTerms={() => setShowTermsModal(true)}
                        />
                        {showTermsModal && <SignUpTermsModal onClose={() => setShowTermsModal(false)} />}
                    </>
                ) : activeTab === 'forgotPassword' ? (
                    <ForgotPasswordForm
                        isLoading={isLoading} onSubmit={handleForgotPasswordSubmit}
                        onBackToLogin={() => handleTabChange('login')} successMessage={successMessage}
                    />
                ) : (
                    <VerificationForm
                        email={verificationEmail} isLoading={isLoading}
                        onResend={handleResendVerification} onBackToLogin={() => setActiveTab('login')}
                    />
                )}
            </div>
        </div>
    );
}

function LoginForm({ formData, showPassword, isLoading, onInputChange, onTogglePassword, onSubmit, onForgotPassword }) {
    return (
        <form className="auth-form" onSubmit={onSubmit}>
            <h2 className="auth-form-title">Log In</h2>
            <div className="form-group">
                <label htmlFor="login-email" className="form-label">Email Address</label>
                <input type="email" id="login-email" name="email" className="form-input" placeholder="Enter your email address" value={formData.email} onChange={onInputChange} disabled={isLoading} autoComplete="email" required />
            </div>
            <div className="form-group">
                <label htmlFor="login-password" className="form-label">Password</label>
            <div className="password-input-wrapper">
                <input type={showPassword ? 'text' : 'password'} id="login-password" name="password" className="form-input" placeholder="Enter your password" value={formData.password} onChange={onInputChange} disabled={isLoading} autoComplete="current-password" required />
                <button type="button" className="password-toggle" onClick={onTogglePassword} disabled={isLoading} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                        {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
            </div>
            <div className="password-label-row">
                <label className="checkbox-label">
                    <input type="checkbox" name="rememberMe" checked={formData.rememberMe} onChange={onInputChange} disabled={isLoading} />
                    <span className="checkbox-custom"></span>
                    <span className="checkbox-text">Remember Me</span>
                </label>
                <button type="button" className="forgot-password-link" onClick={onForgotPassword} disabled={isLoading}>Forgot password?</button>
            </div>
        </div>
            <button type="submit" className="auth-submit-button" disabled={isLoading}>
                {isLoading ? <LoadingSpinner /> : 'Log In'}
            </button>
        </form>
    );
}

function ForgotPasswordForm({ isLoading, onSubmit, onBackToLogin, successMessage }) {
    const [email, setEmail] = useState('');
    const [localError, setLocalError] = useState('');

    const handleSubmit = (event) => {
        event.preventDefault();
        setLocalError('');
        if (!email.trim()) { setLocalError('Please enter your email address.'); return; }
        const emailRegex = /^[a-zA-Z0-9](?:[a-zA-Z0-9._-]*[a-zA-Z0-9])?@[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,10}$/;
        if (!emailRegex.test(email.trim())) { setLocalError('Please enter a valid email address.'); return; }
        onSubmit(email);
    };

    return (
        <form className="auth-form" onSubmit={handleSubmit}>
            <div className="forgot-password-icon"><LockIcon /></div>
            <h2 className="auth-form-title">Forgot Password</h2>
            <p className="forgot-password-description">Enter the email address associated with your account and we'll send you a link to reset your password.</p>
            {localError && <div className="auth-error-message">{localError}</div>}
            {!successMessage && (
                <>
                    <div className="form-group">
                        <label htmlFor="reset-email" className="form-label">Email Address</label>
                        <input type="email" id="reset-email" name="resetEmail" className="form-input" placeholder="Enter your email address" value={email} onChange={(e) => { setEmail(e.target.value); if (localError) setLocalError(''); }} disabled={isLoading} required />
                    </div>
                    <button type="submit" className="auth-submit-button" disabled={isLoading}>
                        {isLoading ? <LoadingSpinner /> : 'Send Reset Link'}
                    </button>
                </>
            )}
            <button type="button" className="back-to-login-button" onClick={onBackToLogin} disabled={isLoading}>Back to Login</button>
        </form>
    );
}

function SignUpForm({ formData, showPassword, isLoading, onInputChange, onEmailChange, onEmailBlur, emailValidation, onPhoneChange, onPhoneBlur, phoneValidation, onPasswordChange, passwordValidation, onTogglePassword, onSubmit, onShowTerms }) {
    const { useEffect } = React;
    
    useEffect(() => {
        const timer = setTimeout(() => {
            if (formData.email) onEmailChange({ target: { value: '' } });
            if (formData.password) onPasswordChange({ target: { value: '' } });
        }, 100);
        return () => clearTimeout(timer);
    }, []);

    const getEmailInputClass  = () => emailValidation.isValid === null ? 'form-input' : emailValidation.isValid ? 'form-input input-valid' : 'form-input input-error';
    const getPhoneInputClass  = () => phoneValidation.isValid === null ? 'form-input' : phoneValidation.isValid ? 'form-input input-valid' : 'form-input input-error';
    const getPasswordInputClass = () => passwordValidation.isValid === null ? 'form-input' : passwordValidation.isValid ? 'form-input input-valid' : 'form-input input-error';

    return (
        <form className="auth-form" onSubmit={onSubmit} autoComplete="off">
            {/* Honeypot inputs — tricks Chromium into filling these instead of real fields */}
            <input type="text" style={{ display: 'none' }} aria-hidden="true" readOnly />
            <input type="email" style={{ display: 'none' }} aria-hidden="true" readOnly />
            <input type="password" style={{ display: 'none' }} aria-hidden="true" readOnly />

            <h2 className="auth-form-title">Create an Account</h2>

            <div className="form-row">
                <div className="form-group">
                    <label htmlFor="signup-firstname" className="form-label">First Name</label>
                    <input type="text" id="signup-firstname" name="firstName" className="form-input" placeholder="Enter your first name" value={formData.firstName} onChange={onInputChange} disabled={isLoading} required />
                </div>
                <div className="form-group">
                    <label htmlFor="signup-lastname" className="form-label">Last Name</label>
                    <input type="text" id="signup-lastname" name="lastName" className="form-input" placeholder="Enter your last name" value={formData.lastName} onChange={onInputChange} disabled={isLoading} required />
                </div>
            </div>

            <div className="form-group">
                <label htmlFor="signup-email" className="form-label">Email Address</label>
                <div className="email-input-wrapper">
                    <input type="email" id="signup-email" name="email" className={getEmailInputClass()} placeholder="Enter your email address" value={formData.email} onChange={onEmailChange} onBlur={onEmailBlur} disabled={isLoading} autoComplete="off" required />
                    {emailValidation.isValid === true && <span className="validation-icon valid-icon"><CheckIcon /></span>}
                    {emailValidation.isValid === false && <span className="validation-icon error-icon"><AlertIcon /></span>}
                </div>
                {emailValidation.isValid === false && emailValidation.message && <span className="field-error-message">{emailValidation.message}</span>}
            </div>

            <div className="form-group">
                <label htmlFor="signup-contact" className="form-label">
                    Contact Number
                    <span className="label-hint">(Philippine numbers only: +63)</span>
                </label>
                <div className="phone-input-wrapper">
                    <input type="tel" id="signup-contact" name="contactNumber" className={getPhoneInputClass()} placeholder="09171234567" value={formData.contactNumber} onChange={onPhoneChange} onBlur={onPhoneBlur} disabled={isLoading} required />
                    {phoneValidation.isValid === true && <span className="validation-icon valid-icon"><CheckIcon /></span>}
                    {phoneValidation.isValid === false && <span className="validation-icon error-icon"><AlertIcon /></span>}
                </div>
                {phoneValidation.isValid === false && phoneValidation.message && <span className="field-error-message">{phoneValidation.message}</span>}
            </div>

            <div className="form-group">
                <label htmlFor="signup-password" className="form-label">Password</label>
                <div className="password-input-wrapper">
                    <input type={showPassword ? 'text' : 'password'} id="signup-password" name="password" className={getPasswordInputClass()} placeholder="Min. 8 chars with uppercase, lowercase & symbol" value={formData.password} onChange={onPasswordChange} disabled={isLoading} autoComplete="new-password" minLength={8} required />
                    <button type="button" className="password-toggle" onClick={onTogglePassword} disabled={isLoading} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                        {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                </div>
                {passwordValidation.isValid === false && passwordValidation.message && <span className="field-error-message">{passwordValidation.message}</span>}
                <span className="password-hint">Password must contain at least 8 characters with uppercase, lowercase letters, and a symbol.</span>
            </div>

            <div className="form-group checkbox-group">
                <label className="checkbox-label">
                    <input type="checkbox" name="agreeToPromotions" checked={formData.agreeToPromotions} onChange={onInputChange} disabled={isLoading} />
                    <span className="checkbox-custom"></span>
                    <span className="checkbox-text promotional-text">
                        I agree to receive promotional emails and updates from JG Printing.                   
                        </span>
                </label>
            </div>

            {/* ── UPDATED: Short, clean terms checkbox ── */}
            <div className="form-group checkbox-group">
                <label className="checkbox-label terms-label">
                    <input type="checkbox" name="agreeToTerms" checked={formData.agreeToTerms} onChange={onInputChange} disabled={isLoading} required />
                    <span className="checkbox-custom"></span>
                    <span className="checkbox-text terms-text">
                        I have read and agree to the{' '}
                        <button type="button" className="terms-link" onClick={onShowTerms} disabled={isLoading}>
                            Terms and Conditions
                        </button>
                    </span>
                </label>
            </div>

            <button type="submit" className="auth-submit-button" disabled={isLoading || !formData.agreeToTerms}>
                {isLoading ? <LoadingSpinner /> : 'Sign Up'}
            </button>
        </form>
    );
}

function SignUpTermsModal({ onClose }) {
    const contentRef = useRef(null);
    const [activePill, setActivePill] = React.useState('data-collection');

    const sections = [
        { id: 'data-collection', label: 'Data Collection' },
        { id: 'data-protection', label: 'Protection' },
        { id: 'marketing',       label: 'Marketing' },
        { id: 'your-rights',     label: 'Your Rights' },
        { id: 'contact',         label: 'Contact' },
    ];

    const scrollToSection = (id) => {
        setActivePill(id);
        const el = contentRef.current?.querySelector(`#terms-${id}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const handleScroll = () => {
        if (!contentRef.current) return;
        const containerTop = contentRef.current.getBoundingClientRect().top;
        for (let i = sections.length - 1; i >= 0; i--) {
            const el = contentRef.current.querySelector(`#terms-${sections[i].id}`);
            if (el && el.getBoundingClientRect().top <= containerTop + 60) {
                setActivePill(sections[i].id);
                break;
            }
        }
    };

    return (
        <div className="terms-modal-overlay" onClick={onClose}>
            <div className="terms-modal" onClick={(e) => e.stopPropagation()}>
                <div className="terms-modal-header">
                    <div className="terms-header-top">
                        <div className="terms-header-icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                <polyline points="14 2 14 8 20 8"/>
                                <line x1="16" y1="13" x2="8" y2="13"/>
                                <line x1="16" y1="17" x2="8" y2="17"/>
                                <polyline points="10 9 9 9 8 9"/>
                            </svg>
                        </div>
                        <div className="terms-title-block">
                            <h2 className="terms-modal-title">Terms and Conditions</h2>
                            <p className="terms-modal-subtitle">JG Printing · Privacy Policy</p>
                        </div>
                        <button className="terms-modal-close" onClick={onClose} aria-label="Close terms modal">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <line x1="18" y1="6" x2="6" y2="18"/>
                                <line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                        </button>
                    </div>
                    <div className="terms-section-pills">
                        {sections.map(s => (
                            <button key={s.id} className={`terms-pill ${activePill === s.id ? 'active' : ''}`} onClick={() => scrollToSection(s.id)}>{s.label}</button>
                        ))}
                    </div>
                </div>

                <div className="terms-modal-content" ref={contentRef} onScroll={handleScroll}>
                    <div className="terms-section" id="terms-data-collection">
                        <div className="terms-section-header"><div className="terms-section-dot"></div><h3>Data Collection and Usage</h3></div>
                        <p>When you sign up with JG Printing, we collect the following information:</p>
                        <ul><li>First and Last Name</li><li>Email Address</li><li>Contact Number (Philippine phone numbers)</li></ul>
                        <p>This information is essential for creating and managing your account, processing your orders, and contacting you regarding your order status.</p>
                    </div>
                    <div className="terms-section" id="terms-data-protection">
                        <div className="terms-section-header"><div className="terms-section-dot"></div><h3>Data Protection and Security</h3></div>
                        <p>Your personal information is securely stored on our servers using industry-standard security measures to protect against unauthorized access, alteration, or disclosure.</p>
                        <div className="terms-highlight-box">We take reasonable measures to ensure your information will not be leaked or shared with third parties without your explicit consent.</div>
                        <p>Your data is only used for:</p>
                        <ul><li>Account creation and management</li><li>Order processing and delivery coordination</li><li>Payment processing</li><li>Customer service and support</li></ul>
                    </div>
                    <div className="terms-section" id="terms-marketing">
                        <div className="terms-section-header"><div className="terms-section-dot"></div><h3>Marketing and Communications</h3></div>
                        <p>By signing up, you understand that we may use your contact details for marketing purposes, including:</p>
                        <ul><li>New products and services announcements</li><li>Special offers, discounts, and promotions</li><li>Company news and events</li><li>Feedback and review requests</li></ul>
                        <p>You may opt-out of marketing emails at any time via the unsubscribe link in any email, or by contacting us at jgprinting2024@gmail.com.</p>
                    </div>
                    <div className="terms-section" id="terms-your-rights">
                        <div className="terms-section-header"><div className="terms-section-dot"></div><h3>Your Rights</h3></div>
                        <p>You have the right to:</p>
                        <ul><li>Access your personal information</li><li>Request corrections to your data</li><li>Request deletion of your account and data (where permitted by law)</li><li>Opt-out of marketing communications</li><li>File a complaint regarding data handling practices</li></ul>
                    </div>
                    <div className="terms-section" id="terms-contact">
                        <div className="terms-section-header"><div className="terms-section-dot"></div><h3>Contact Us</h3></div>
                        <p>For questions about our Terms, Privacy Policy, or how we handle your personal information, reach us at:</p>
                        <div className="terms-contact-grid">
                            <div className="terms-contact-row"><span className="terms-contact-label">Email</span><span>jgprinting2024@gmail.com</span></div>
                            <div className="terms-contact-row"><span className="terms-contact-label">Facebook</span><span>JG Printing</span></div>
                            <div className="terms-contact-row"><span className="terms-contact-label">Address</span><span>#16 Camatchile St., Purok 4 Lower Bicutan, Taguig City, Philippines</span></div>
                        </div>
                    </div>
                </div>

                <div className="terms-modal-footer">
                    <p className="terms-footer-note">By clicking "Sign Up" you confirm you have read and agree to these terms.</p>
                    <button className="terms-modal-close-button" onClick={onClose}>Got it</button>
                </div>
            </div>
        </div>
    );
}

function VerificationForm({ email, isLoading, onResend, onBackToLogin }) {
    return (
        <div className="auth-form verification-form">
            <div className="verification-icon"><MailIcon /></div>
            <h2 className="auth-form-title">Verify Your Email</h2>
            <p className="verification-message">We've sent a verification link to:</p>
            <p className="verification-email">{email}</p>
            <p className="verification-instructions">Click the link in the email to verify your account. Once verified, you can log in to access your account.</p>
            <div className="verification-actions">
                <button type="button" className="auth-submit-button resend-button" onClick={onResend} disabled={isLoading}>
                    {isLoading ? <LoadingSpinner /> : 'Resend Verification Email'}
                </button>
                <button type="button" className="back-to-login-button" onClick={onBackToLogin} disabled={isLoading}>Back to Login</button>
            </div>
        </div>
    );
}

function CloseIcon() { return <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>; }
function EyeIcon() { return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>; }
function EyeOffIcon() { return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>; }
function MailIcon() { return <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>; }
function LockIcon() { return <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>; }
function LoadingSpinner() { return <svg className="loading-spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" strokeOpacity="0.25"/><path d="M12 2a10 10 0 0110 10" strokeLinecap="round"/></svg>; }
function EmailIcon() { return <img src={emailIcon} alt="Check your email" className="email-icon-image" />; }
function CheckIcon() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>; }
function AlertIcon() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>; }

export default AuthModal;